<template>
  <div class="payment-links-timeline">
    <!-- Loading state -->
    <div v-if="isLoading" class="timeline-loading">
      <i class="pi pi-spin pi-spinner" style="font-size: 1.5rem;"></i>
      <span>{{ t('Carregando histórico...') }}</span>
    </div>

    <!-- Empty state -->
    <div v-else-if="!events || events.length === 0" class="timeline-empty">
      <i class="pi pi-history" style="font-size: 2rem; color: var(--text-color-secondary);"></i>
      <p>{{ t('Nenhum evento registrado') }}</p>
    </div>

    <!-- Timeline -->
    <Timeline v-else :value="events" align="left" class="custom-timeline">
      <template #marker="slotProps">
        <span
          class="timeline-marker"
          :class="getEventTypeClass((slotProps.item as TimelineEvent).event_type)"
        >
          <i :class="getEventIcon((slotProps.item as TimelineEvent).event_type)"></i>
        </span>
      </template>
      <template #content="slotProps">
        <div class="timeline-event">
          <div class="event-header">
            <span class="event-type">{{ getEventTypeLabel((slotProps.item as TimelineEvent).event_type) }}</span>
            <span class="event-date">{{ formatDateTime((slotProps.item as TimelineEvent).created_at) }}</span>
          </div>
          <p class="event-description" v-if="(slotProps.item as TimelineEvent).event_description">
            {{ (slotProps.item as TimelineEvent).event_description }}
          </p>
        </div>
      </template>
    </Timeline>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';

interface TimelineEvent {
  event_type?: string;
  event_description?: string;
  created_at?: string;
  [key: string]: unknown;
}

interface EventTypeConfig {
  icon: string;
  label: string;
  class: string;
}

withDefaults(
  defineProps<{
    events?: TimelineEvent[];
    isLoading?: boolean;
  }>(),
  {
    events: () => [],
    isLoading: false,
  }
);

const { t } = useI18n();

// Event type configuration - maps backend event_type to display labels and icons
const eventTypeConfig: Record<string, EventTypeConfig> = {
  created: {
    icon: 'pi pi-plus-circle',
    label: 'Link Criado',
    class: 'event-created'
  },
  notification_sent: {
    icon: 'pi pi-send',
    label: 'Notificacao Enviada',
    class: 'event-notification'
  },
  viewed: {
    icon: 'pi pi-eye',
    label: 'Link Visualizado',
    class: 'event-viewed'
  },
  payment_attempted: {
    icon: 'pi pi-credit-card',
    label: 'Tentativa de Pagamento',
    class: 'event-attempted'
  },
  payment_failed: {
    icon: 'pi pi-times-circle',
    label: 'Pagamento Falhou',
    class: 'event-failed'
  },
  payment_confirmed: {
    icon: 'pi pi-check-circle',
    label: 'Pagamento Confirmado',
    class: 'event-confirmed'
  },
  partially_paid: {
    icon: 'pi pi-minus-circle',
    label: 'Pagamento Parcial',
    class: 'event-partial'
  },
  expired: {
    icon: 'pi pi-clock',
    label: 'Link Expirado',
    class: 'event-expired'
  },
  cancelled: {
    icon: 'pi pi-ban',
    label: 'Link Cancelado',
    class: 'event-cancelled'
  },
  refund_requested: {
    icon: 'pi pi-replay',
    label: 'Estorno Solicitado',
    class: 'event-refund'
  },
  refunded: {
    icon: 'pi pi-check',
    label: 'Estorno Concluido',
    class: 'event-refunded'
  },
  installment_paid: {
    icon: 'pi pi-wallet',
    label: 'Parcela Paga',
    class: 'event-installment'
  },
  payment_synced: {
    icon: 'pi pi-sync',
    label: 'Sincronizado Manualmente',
    class: 'event-synced'
  },
  refund_processed: {
    icon: 'pi pi-check',
    label: 'Estorno Processado',
    class: 'event-refunded'
  }
};

// Methods
const getEventTypeLabel = (eventType?: string): string => {
  const config = eventTypeConfig[eventType || ''];
  return config ? t(config.label) : (eventType || '');
};

const getEventIcon = (eventType?: string): string => {
  const config = eventTypeConfig[eventType || ''];
  return config ? config.icon : 'pi pi-circle';
};

const getEventTypeClass = (eventType?: string): string => {
  const config = eventTypeConfig[eventType || ''];
  return config ? config.class : 'event-default';
};

const formatDateTime = (dateStr?: string): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};
</script>

<style scoped>
.payment-links-timeline {
  padding: 1rem 0;
}

.timeline-loading,
.timeline-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  gap: 0.5rem;
  color: var(--text-color-secondary);
}

.custom-timeline {
  padding: 0;
}

.custom-timeline :deep(.p-timeline-event-opposite) {
  display: none;
}

.timeline-marker {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background-color: var(--surface-ground);
  border: 2px solid var(--surface-border);
}

.timeline-marker i {
  font-size: 0.875rem;
}

/* Event type marker colors */
.event-created { background-color: #e3f2fd; border-color: #2196f3; color: #1565c0; }
.event-notification { background-color: #e8f5e9; border-color: #4caf50; color: #2e7d32; }
.event-viewed { background-color: #f3e5f5; border-color: #9c27b0; color: #7b1fa2; }
.event-attempted { background-color: #fff3e0; border-color: #ff9800; color: #e65100; }
.event-failed { background-color: #ffebee; border-color: #f44336; color: #c62828; }
.event-confirmed { background-color: #e8f5e9; border-color: #4caf50; color: #2e7d32; }
.event-partial { background-color: #fffde7; border-color: #ffeb3b; color: #f57f17; }
.event-expired { background-color: #fff3e0; border-color: #ff9800; color: #e65100; }
.event-cancelled { background-color: #ffebee; border-color: #f44336; color: #c62828; }
.event-refund { background-color: #eceff1; border-color: #607d8b; color: #37474f; }
.event-refunded { background-color: #eceff1; border-color: #607d8b; color: #37474f; }
.event-installment { background-color: #e3f2fd; border-color: #2196f3; color: #1565c0; }
.event-synced { background-color: #e0f7fa; border-color: #00bcd4; color: #00838f; }
.event-default { background-color: var(--surface-ground); border-color: var(--surface-border); }

.timeline-event {
  padding: 0.5rem 0;
}

.event-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.25rem;
}

.event-type {
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--text-color);
}

.event-date {
  font-size: 0.75rem;
  color: var(--text-color-secondary);
}

.event-description {
  margin: 0.25rem 0;
  font-size: 0.875rem;
  color: var(--text-color-secondary);
}

</style>
